import React, { useState, useEffect } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { Header } from '../components/Layout';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { toast } from 'sonner';
import { Eye, EyeOff, Mail, Lock, User, Phone, Globe, RefreshCw, CheckCircle2, Gift } from 'lucide-react';

const NEW_LOGO_URL = "https://customer-assets.emergentagent.com/job_krazy-bets-preview/artifacts/pvmmvvvw__krazy%20bets%20logo%20Final%20png1.png";

const COUNTRIES = [
  { name: "South Africa", code: "+27", flag: "ZA" },
  { name: "United States", code: "+1", flag: "US" },
  { name: "United Kingdom", code: "+44", flag: "GB" },
  { name: "Nigeria", code: "+234", flag: "NG" },
  { name: "Kenya", code: "+254", flag: "KE" },
  { name: "India", code: "+91", flag: "IN" },
  { name: "Brazil", code: "+55", flag: "BR" },
  { name: "Germany", code: "+49", flag: "DE" },
  { name: "France", code: "+33", flag: "FR" },
  { name: "Australia", code: "+61", flag: "AU" },
  { name: "Canada", code: "+1", flag: "CA" },
  { name: "Ghana", code: "+233", flag: "GH" },
  { name: "Zimbabwe", code: "+263", flag: "ZW" },
  { name: "Botswana", code: "+267", flag: "BW" },
  { name: "Mozambique", code: "+258", flag: "MZ" },
  { name: "Tanzania", code: "+255", flag: "TZ" },
  { name: "Uganda", code: "+256", flag: "UG" },
  { name: "Egypt", code: "+20", flag: "EG" },
  { name: "Morocco", code: "+212", flag: "MA" },
  { name: "Japan", code: "+81", flag: "JP" },
  { name: "China", code: "+86", flag: "CN" },
  { name: "Mexico", code: "+52", flag: "MX" },
  { name: "Spain", code: "+34", flag: "ES" },
  { name: "Italy", code: "+39", flag: "IT" },
  { name: "Netherlands", code: "+31", flag: "NL" },
  { name: "Portugal", code: "+351", flag: "PT" },
  { name: "Sweden", code: "+46", flag: "SE" },
  { name: "Norway", code: "+47", flag: "NO" },
  { name: "UAE", code: "+971", flag: "AE" },
  { name: "Saudi Arabia", code: "+966", flag: "SA" },
].sort((a, b) => a.name.localeCompare(b.name));

const generateCaptcha = () => {
  const chars = 'ABCDEFGHJKLMNPQRSTUVWXYZ23456789';
  let result = '';
  for (let i = 0; i < 6; i++) result += chars.charAt(Math.floor(Math.random() * chars.length));
  return result;
};

const formatApiError = (detail) => {
  if (detail == null) return "Something went wrong. Please try again.";
  if (typeof detail === "string") return detail;
  if (Array.isArray(detail))
    return detail.map((e) => (e && typeof e.msg === "string" ? e.msg : JSON.stringify(e))).filter(Boolean).join(" ");
  if (detail && typeof detail.msg === "string") return detail.msg;
  return String(detail);
};

const RegisterPage = () => {
  const { register } = useAuth();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const refCode = (searchParams.get('ref') || '').trim().toUpperCase();
  const [fullName, setFullName] = useState('');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [registered, setRegistered] = useState(false);

  // Country & phone
  const [selectedCountry, setSelectedCountry] = useState(null);
  const [countrySearch, setCountrySearch] = useState('');
  const [showCountryDropdown, setShowCountryDropdown] = useState(false);
  const [phoneNumber, setPhoneNumber] = useState('');

  // Captcha
  const [captchaCode, setCaptchaCode] = useState(generateCaptcha());
  const [captchaInput, setCaptchaInput] = useState('');

  const filteredCountries = COUNTRIES.filter(c =>
    c.name.toLowerCase().includes(countrySearch.toLowerCase()) ||
    c.code.includes(countrySearch)
  );

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');

    if (password !== confirmPassword) {
      setError("Passwords don't match");
      return;
    }
    if (password.length < 6) {
      setError("Password must be at least 6 characters");
      return;
    }
    if (!selectedCountry) {
      setError("Please select your country");
      return;
    }
    if (captchaInput.toUpperCase() !== captchaCode) {
      setError("CAPTCHA verification failed. Please try again.");
      setCaptchaCode(generateCaptcha());
      setCaptchaInput('');
      return;
    }

    setLoading(true);
    try {
      await register(email, password, fullName, {
        country: selectedCountry?.name,
        mobile_number: phoneNumber ? `${selectedCountry?.code}${phoneNumber}` : undefined,
        ref: refCode || undefined,
      });
      setRegistered(true);
      toast.success(refCode ? 'Account created! You got a +50 QBC referral bonus 🎉' : 'Account created! Please verify your email.');
    } catch (err) {
      const errorMsg = formatApiError(err.response?.data?.detail);
      setError(errorMsg);
      toast.error(errorMsg);
    } finally {
      setLoading(false);
    }
  };

  const handleVerifyEmail = () => {
    // Mocked: auto-verify and redirect to login
    toast.success('Email verified successfully! You can now login.');
    navigate('/login');
  };

  if (registered) {
    return (
      <div className="min-h-screen bg-[#1A1A1A]">
        <Header showAuth={false} />
        <div className="flex items-center justify-center py-20 px-4">
          <div className="w-full max-w-md">
            <div className="card-dark text-center">
              <div className="w-20 h-20 mx-auto mb-6 rounded-full bg-[#D4AF37]/20 flex items-center justify-center">
                <Mail className="h-10 w-10 text-[#D4AF37]" />
              </div>
              <h1 className="text-2xl font-black uppercase text-white mb-4" style={{ fontFamily: 'Outfit' }}>
                Verify Your Email
              </h1>
              <p className="text-gray-400 mb-6">
                Please verify your email address before you can login. We've sent a verification link to <span className="text-[#D4AF37] font-semibold">{email}</span>
              </p>
              <Button onClick={handleVerifyEmail} className="btn-primary w-full" data-testid="verify-email-btn">
                <CheckCircle2 className="h-5 w-5 mr-2" /> I've Verified My Email
              </Button>
              <p className="text-xs text-gray-500 mt-4">
                Didn't receive the email? Check your spam folder or click above to continue.
              </p>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#1A1A1A]">
      <Header showAuth={false} />
      
      <div className="flex items-center justify-center py-8 px-4">
        <div className="w-full max-w-md">
          <div className="card-dark">
            <div className="text-center mb-6">
              <img src={NEW_LOGO_URL} alt="KrazyBets" className="h-16 w-16 mx-auto mb-3" />
              <h1 className="text-2xl font-black uppercase text-white" style={{ fontFamily: 'Outfit' }}>
                Join <span className="text-[#D4AF37]">KrazyBets</span>
              </h1>
              <p className="text-gray-400 mt-1 text-sm">Get 100 free Qbean tokens on signup!</p>
            </div>

            {refCode && (
              <div className="mb-4 p-3 rounded-lg border border-[#D4AF37]/50 bg-[#D4AF37]/10 flex items-start gap-2" data-testid="referral-banner">
                <Gift className="h-5 w-5 text-[#D4AF37] mt-0.5 flex-shrink-0" />
                <div>
                  <p className="text-sm text-white font-bold">You were invited! Code: <span className="font-mono text-[#D4AF37]">{refCode}</span></p>
                  <p className="text-xs text-gray-300">Claim an extra <span className="text-[#D4AF37] font-bold">+50 QBC bonus</span> on signup, and your inviter gets 50 QBC too.</p>
                </div>
              </div>
            )}

            {error && (
              <div className="bg-red-500/10 border border-red-500 rounded-lg p-3 mb-4 text-red-400 text-sm">
                {error}
              </div>
            )}

            <form onSubmit={handleSubmit} className="space-y-4">
              <div>
                <Label className="font-semibold text-white text-sm">Full Name</Label>
                <div className="relative mt-1">
                  <User className="absolute left-4 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-500" />
                  <Input value={fullName} onChange={(e) => setFullName(e.target.value)} placeholder="John Doe" className="input-dark" style={{ paddingLeft: '3rem' }} required data-testid="register-name-input" />
                </div>
              </div>

              <div>
                <Label className="font-semibold text-white text-sm">Email</Label>
                <div className="relative mt-1">
                  <Mail className="absolute left-4 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-500" />
                  <Input type="email" value={email} onChange={(e) => setEmail(e.target.value)} placeholder="you@example.com" className="input-dark" style={{ paddingLeft: '3rem' }} required data-testid="register-email-input" />
                </div>
              </div>

              {/* Country Selector */}
              <div>
                <Label className="font-semibold text-white text-sm">Country</Label>
                <div className="relative mt-1">
                  <Globe className="absolute left-4 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-500 z-10" />
                  <Input
                    value={selectedCountry ? selectedCountry.name : countrySearch}
                    onChange={(e) => { setCountrySearch(e.target.value); setSelectedCountry(null); setShowCountryDropdown(true); }}
                    onFocus={() => setShowCountryDropdown(true)}
                    placeholder="Search your country..."
                    className="input-dark"
                    style={{ paddingLeft: '3rem' }}
                    data-testid="register-country-input"
                  />
                  {showCountryDropdown && (
                    <div className="absolute top-full left-0 right-0 mt-1 bg-[#2D2D2D] border border-[#3D3D3D] rounded-lg max-h-48 overflow-y-auto z-50">
                      {filteredCountries.map((c) => (
                        <button
                          key={c.flag}
                          type="button"
                          onClick={() => { setSelectedCountry(c); setCountrySearch(''); setShowCountryDropdown(false); }}
                          className="w-full text-left px-4 py-2 text-sm text-white hover:bg-[#3D3D3D] flex items-center justify-between"
                        >
                          <span>{c.name}</span>
                          <span className="text-gray-400">{c.code}</span>
                        </button>
                      ))}
                    </div>
                  )}
                </div>
              </div>

              {/* Phone Number */}
              <div>
                <Label className="font-semibold text-white text-sm">Mobile Number</Label>
                <div className="flex gap-2 mt-1">
                  <div className="bg-[#2D2D2D] border-2 border-[#3D3D3D] rounded-lg px-3 py-2 text-white font-semibold text-sm min-w-[70px] text-center flex items-center justify-center">
                    {selectedCountry ? selectedCountry.code : '--'}
                  </div>
                  <div className="relative flex-1">
                    <Phone className="absolute left-4 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-500" />
                    <Input value={phoneNumber} onChange={(e) => setPhoneNumber(e.target.value.replace(/[^0-9]/g, ''))} placeholder="Phone number" className="input-dark" style={{ paddingLeft: '3rem' }} data-testid="register-phone-input" />
                  </div>
                </div>
              </div>

              <div>
                <Label className="font-semibold text-white text-sm">Password</Label>
                <div className="relative mt-1">
                  <Lock className="absolute left-4 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-500" />
                  <Input type={showPassword ? "text" : "password"} value={password} onChange={(e) => setPassword(e.target.value)} placeholder="Min 6 characters" className="input-dark" style={{ paddingLeft: '3rem', paddingRight: '3rem' }} required data-testid="register-password-input" />
                  <button type="button" onClick={() => setShowPassword(!showPassword)} className="absolute right-4 top-1/2 -translate-y-1/2 text-gray-500 hover:text-[#D4AF37]">
                    {showPassword ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                  </button>
                </div>
              </div>

              <div>
                <Label className="font-semibold text-white text-sm">Confirm Password</Label>
                <div className="relative mt-1">
                  <Lock className="absolute left-4 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-500" />
                  <Input type={showPassword ? "text" : "password"} value={confirmPassword} onChange={(e) => setConfirmPassword(e.target.value)} placeholder="Confirm password" className="input-dark" style={{ paddingLeft: '3rem' }} required data-testid="register-confirm-password-input" />
                </div>
              </div>

              {/* CAPTCHA */}
              <div>
                <Label className="font-semibold text-white text-sm">Security Verification</Label>
                <div className="flex items-center gap-3 mt-1">
                  <div className="bg-[#0F0F0F] px-4 py-2 rounded-lg border border-[#3D3D3D] select-none" style={{ fontFamily: 'monospace', letterSpacing: '0.3em', fontSize: '1.2rem', color: '#D4AF37', fontWeight: 'bold', textDecoration: 'line-through', fontStyle: 'italic' }} data-testid="captcha-code">
                    {captchaCode}
                  </div>
                  <button type="button" onClick={() => { setCaptchaCode(generateCaptcha()); setCaptchaInput(''); }} className="p-2 text-gray-400 hover:text-[#D4AF37]" data-testid="refresh-captcha">
                    <RefreshCw className="h-4 w-4" />
                  </button>
                  <Input value={captchaInput} onChange={(e) => setCaptchaInput(e.target.value.toUpperCase())} placeholder="Enter code" className="input-dark flex-1" maxLength={6} data-testid="captcha-input" />
                </div>
              </div>

              <div className="flex items-start gap-2">
                <input type="checkbox" id="terms" required className="mt-1 h-4 w-4 border-2 border-[#D4AF37] rounded bg-[#2D2D2D]" data-testid="register-terms-checkbox" />
                <Label htmlFor="terms" className="text-xs text-gray-400">
                  I am 18+ and agree to the <Link to="/terms" target="_blank" className="text-[#D4AF37] hover:underline" data-testid="register-terms-link">Terms of Service</Link> and <Link to="/responsible-gambling" target="_blank" className="text-[#D4AF37] hover:underline" data-testid="register-responsible-link">Responsible Gaming Policy</Link>
                </Label>
              </div>

              <Button type="submit" className="btn-primary w-full" disabled={loading} data-testid="register-submit-btn">
                {loading ? 'Creating Account...' : 'Create Account — It\'s Free'}
              </Button>
            </form>

            <div className="mt-4 text-center">
              <p className="text-gray-400 text-sm">
                Already have an account? <Link to="/login" className="text-[#D4AF37] font-semibold hover:underline">Login</Link>
              </p>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default RegisterPage;
